<script setup>
import { computed } from 'vue'
import { useOverviewStore } from '../../../../../state/overviewStore.js'
import { trans } from 'laravel-vue-i18n'

const store   = useOverviewStore()
const heatmap = computed(() => store.currentHeatmap?.matrix ?? [])
const allDay  = computed(() => store.currentHeatmap?.allDay ?? [])

const DOW_LABELS  = [trans('common.dow_short.mon'), trans('common.dow_short.tue'), trans('common.dow_short.wed'), trans('common.dow_short.thu'), trans('common.dow_short.fri'), trans('common.dow_short.sat'), trans('common.dow_short.sun')]
const HEAT_HOURS  = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19]

function lvlColor(v) {
    if (v === 0) return '#EFEFF1'
    const pct = 20 + v * 20
    return `color-mix(in oklch, #2A6FDB ${pct}%, white)`
}

function allDayColor(v) {
    if (v === 0) return '#EFEFF1'
    const pct = 20 + v * 20
    return `color-mix(in oklch, #7C3AED ${pct}%, white)`
}
</script>

<template>
    <div>
        <div class="overflow-x-auto">
            <div class="grid gap-1" :style="{ gridTemplateColumns: '34px repeat(12, 1fr) 6px 48px', minWidth: '420px' }">
                <!-- Header row -->
                <div/>
                <div
                    v-for="h in HEAT_HOURS"
                    :key="h"
                    class="text-[10px] text-gray-300 dark:text-neutral-600 text-center font-semibold h-[18px] flex items-center justify-center"
                >{{ h }}</div>
                <div/>
                <div class="text-[9px] text-gray-400 dark:text-neutral-500 font-semibold flex items-center justify-center leading-tight text-center">{{ $t('dashboard.heatmap_all_day') }}</div>

                <!-- Data rows -->
                <template v-for="(row, ri) in (heatmap.length ? heatmap : Array(7).fill(Array(12).fill(0)))" :key="ri">
                    <div class="text-[11px] text-gray-400 dark:text-neutral-500 font-semibold flex items-center h-6">{{ DOW_LABELS[ri] }}</div>
                    <div
                        v-for="(v, ci) in row"
                        :key="ci"
                        v-tooltip.top="$t('dashboard.heatmap_tooltip', { day: DOW_LABELS[ri], hour: HEAT_HOURS[ci], count: v })"
                        class="h-6 rounded-[6px] cursor-default transition-opacity hover:opacity-80"
                        :class="v === 0 ? 'bg-[#EFEFF1] dark:bg-neutral-800' : ''"
                        :style="v > 0 ? { background: lvlColor(v) } : {}"
                    />
                    <!-- Separator -->
                    <div/>
                    <!-- All-day cell -->
                    <div
                        v-tooltip.top="$t('dashboard.heatmap_tooltip_allday', { day: DOW_LABELS[ri] })"
                        class="h-6 rounded-[6px] cursor-default transition-opacity hover:opacity-80"
                        :class="(allDay[ri] ?? 0) === 0 ? 'bg-[#EFEFF1] dark:bg-neutral-800' : ''"
                        :style="(allDay[ri] ?? 0) > 0 ? { background: allDayColor(allDay[ri]) } : {}"
                    />
                </template>
            </div>
        </div>

        <!-- Legend -->
        <div class="flex items-center gap-3 justify-end mt-3 text-[11px] text-gray-400 dark:text-neutral-500 font-medium">
            <div class="flex items-center gap-1.5">
                {{ $t('dashboard.heatmap_less') }}
                <span
                    v-for="v in [0,1,2,3,4]"
                    :key="v"
                    class="w-4 h-3 rounded-[3px]"
                    :class="v === 0 ? 'bg-[#EFEFF1] dark:bg-neutral-800' : ''"
                    :style="v > 0 ? { background: lvlColor(v) } : {}"
                />
                {{ $t('dashboard.heatmap_more') }}
            </div>
            <span class="text-gray-200 dark:text-neutral-700">|</span>
            <div class="flex items-center gap-1.5">
                <span class="w-4 h-3 rounded-[3px]" :style="{ background: allDayColor(3) }"/>
                {{ $t('dashboard.heatmap_all_day_legend') }}
            </div>
        </div>
    </div>
</template>
